import { useEffect, useRef, useState } from 'react';
import type { AdvisorMessage, AdvisorUsageView } from '@clarity/contract';
import { useClientContext } from '../../lib/clientContext.js';
import { useI18n } from '../../lib/i18n.js';
import { useAdvisorThread, useMyAdvisorUsage, useSendAdvisorMessage } from '../../queries/index.js';
import { IconSpark } from '../../components/icons.js';

/** The send arrow — drawn, direction-aware (flips in RTL via `.ico-flip`). */
function IconSend(p: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M5 12h13M12 6l6 6-6 6" />
    </svg>
  );
}

/**
 * The global Clarity Advisor chat (clarity-advisor-chat Req 6). A launcher on
 * every authenticated owner page opens a slide-up panel that renders the SAME
 * structured `AdvisorReply` blocks the WhatsApp advisor sends — because the
 * server answers both channels with one engine. Built to the signed-off mock
 * `brand/advisor-chat-panel-mock.html`. The browser talks only to
 * `clarity-server` (never Chatwoot). Bilingual + RTL, real tokens, a11y.
 *
 * Owner-only: a client principal sees it. An admin at the top level does not
 * (the advisor answers from one client's data), so the launcher hides when there
 * is no owner client in scope.
 */
export function AdvisorChat() {
  const { activeClientId, selectedClient } = useClientContext();
  const [open, setOpen] = useState(false);

  // The advisor answers for ONE client at a time. `activeClientId` is the
  // client in scope: a client's own tenant, or — for an admin — the client
  // workspace they have open (the `/c/:clientId` route). No client in scope
  // (an admin at the top level) ⇒ no launcher.
  if (!activeClientId) return null;

  return (
    <>
      {!open && <AdvisorLauncher onOpen={() => setOpen(true)} />}
      {open && (
        <AdvisorPanel
          clientId={activeClientId}
          clientName={selectedClient?.name ?? null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AdvisorLauncher({ onOpen }: { onOpen: () => void }) {
  const { t } = useI18n();
  return (
    <button type="button" className="advisor-launcher" onClick={onOpen} aria-label={t('advisor.launch')}>
      <span className="advisor-launcher__ic" aria-hidden="true">
        <IconSpark />
      </span>
      <span>{t('advisor.launch')}</span>
    </button>
  );
}

function AdvisorPanel({
  clientId,
  clientName,
  onClose,
}: {
  clientId: string;
  clientName: string | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const thread = useAdvisorThread(clientId, true);
  const usage = useMyAdvisorUsage(clientId, true);
  const send = useSendAdvisorMessage(clientId);
  const [draft, setDraft] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const messages: AdvisorMessage[] = thread.data?.status === 200 ? thread.data.body.messages : [];
  const pending = send.isPending;
  // Live (human/team) chat availability from the server (Chatwoot configured +
  // a Website inbox). Default true until the thread loads so we don't flash the
  // unavailable banner; the AI advisor answers regardless of this flag.
  const liveChatAvailable =
    thread.data?.status === 200 ? thread.data.body.liveChatAvailable : true;
  // A human agent has taken over in Chatwoot (assigned themselves). The AI is
  // silent; the customer's messages are relayed to the agent, who replies.
  const humanHandled =
    thread.data?.status === 200 ? thread.data.body.conversation.humanHandled : false;

  // Focus the composer on open; Escape closes (a11y).
  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Keep the thread scrolled to the latest message / typing indicator.
  useEffect(() => {
    const el = bodyRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
    else if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  function submit(text: string) {
    const trimmed = text.trim();
    if (trimmed === '' || pending || !liveChatAvailable) return;
    setDraft('');
    send.mutate({ body: { text: trimmed, clientId } });
  }

  // A chip/branch-picker tap: send the raw key as `text` (the server resolves
  // the intent/branch exactly from it) but a human label as `displayText` so the
  // user's bubble reads cleanly instead of showing a raw key like `whats_to_join`.
  function submitChip(intent: string, label: string) {
    if (pending || !liveChatAvailable) return;
    send.mutate({ body: { text: intent, displayText: label, clientId } });
  }

  const starters = [
    t('advisor.starter.status'),
    t('advisor.starter.join'),
    t('advisor.starter.balance'),
    t('advisor.starter.brief'),
  ];

  return (
    <section className="advisor-panel" role="dialog" aria-modal="false" aria-label={t('advisor.title')}>
      <header className="advisor-panel__head">
        <span className="advisor-panel__av" aria-hidden="true">
          <IconSpark />
        </span>
        <div>
          <div className="advisor-panel__title">{humanHandled ? t('advisor.teamName') : t('advisor.title')}</div>
          <div className="advisor-panel__sub">
            <span className="advisor-panel__live" aria-hidden="true" />
            {humanHandled ? t('advisor.humanSubtitle') : t('advisor.subtitle')}
          </div>
        </div>
        <button type="button" className="advisor-panel__x" onClick={onClose} aria-label={t('advisor.close')}>
          ×
        </button>
      </header>

      <div className="advisor-panel__body" ref={bodyRef} aria-live="polite">
        <div className="advisor-panel__channel">
          {clientName ? t('advisor.provenanceFor', { client: clientName }) : t('advisor.provenance')}
        </div>

        {!liveChatAvailable && (
          <div className="advisor-panel__unavailable" role="status">
            {t('advisor.unavailable')}
          </div>
        )}

        {humanHandled && (
          <div className="advisor-panel__human" role="status">
            {t('advisor.humanBanner')}
          </div>
        )}

        {messages.length === 0 && !pending && (
          <div className="advisor-panel__empty">
            <div className="advisor-panel__big" aria-hidden="true">
              <IconSpark />
            </div>
            <h3>{t('advisor.emptyTitle')}</h3>
            <p>{t('advisor.emptyBody')}</p>
            <div className="advisor-panel__starters">
              {starters.map((q) => (
                <button key={q} type="button" onClick={() => submit(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) =>
          m.sender === 'human' ? (
            // A HUMAN agent's reply (merged from Chatwoot after takeover): plain
            // text, attributed to the Clarity team, no AI blocks.
            <div key={m.id} dir="auto" className="advisor-bubble advisor-bubble--them advisor-bubble--human">
              <span className="advisor-bubble__who">{m.senderName || t('advisor.teamName')}</span>
              {m.text}
            </div>
          ) : m.direction === 'inbound' ? (
            <div key={m.id} dir="auto" className="advisor-bubble advisor-bubble--me">
              {m.text}
            </div>
          ) : (
            // AI reply (pure B2): the plain text bubble, then its CONTEXTUAL
            // quick-reply chips parsed from the message (WhatsApp-style).
            <AiMessage key={m.id} message={m} onChip={submitChip} />
          ),
        )}

        {pending && (
          <div className="advisor-typing" aria-label={t('advisor.typing')}>
            <span />
            <span />
            <span />
          </div>
        )}

        {send.isError && (
          <p className="error" role="alert">
            {t('advisor.error')}
          </p>
        )}
      </div>

      {usage.data?.status === 200 && <UsageMeter usage={usage.data.body} />}

      <div className="advisor-composer">
        <textarea
          ref={inputRef}
          rows={1}
          value={draft}
          placeholder={liveChatAvailable ? t('advisor.placeholder') : t('advisor.unavailable')}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit(draft);
            }
          }}
          disabled={!liveChatAvailable}
          aria-label={t('advisor.placeholder')}
        />
        <button
          type="button"
          className="advisor-composer__send"
          onClick={() => submit(draft)}
          disabled={draft.trim() === '' || pending || !liveChatAvailable}
          aria-label={t('advisor.send')}
        >
          <IconSend className="ico-flip" />
        </button>
      </div>
    </section>
  );
}

/**
 * The workspace AI-budget meter (clarity-advisor-llm Req 4.4). A thin bar + a
 * line of copy: the remaining budget normally, a low-budget nudge under 20%,
 * and the cap-reached note when exhausted (the free deterministic chips still
 * work — only the free-form LLM answer is blocked). Per WORKSPACE, so every user
 * of the client shares one budget.
 */
function UsageMeter({ usage }: { usage: AdvisorUsageView }) {
  const { t } = useI18n();
  const cap = usage.capUsd;
  const spent = Math.min(usage.spentUsd, cap);
  const remaining = Math.max(usage.remainingUsd, 0);
  const pct = cap > 0 ? Math.min(100, Math.round((spent / cap) * 100)) : 100;
  const reached = remaining <= 0 && cap > 0;
  const low = !reached && cap > 0 && remaining / cap <= 0.2;
  const money = (n: number) => `$${n.toFixed(2)}`;

  const label = reached
    ? t('advisor.ai.capReached')
    : low
      ? t('advisor.ai.low', { remaining: money(remaining) })
      : t('advisor.ai.remaining', { remaining: money(remaining), cap: money(cap) });

  const state = reached ? 'reached' : low ? 'low' : 'ok';
  return (
    <div className={`advisor-usage advisor-usage--${state}`} role="status">
      <div className="advisor-usage__bar" aria-hidden="true">
        <span className="advisor-usage__fill" style={{ inlineSize: `${pct}%` }} />
      </div>
      <div className="advisor-usage__label">{label}</div>
    </div>
  );
}

/**
 * One AI message (pure B2): the plain-text bubble read back from Chatwoot, then
 * its CONTEXTUAL quick-reply chips (WhatsApp-style per-message), parsed from the
 * message's hidden marker into `reply.blocks` by the server. Tapping a chip
 * sends its intent + label.
 */
function AiMessage({
  message,
  onChip,
}: {
  message: AdvisorMessage;
  onChip: (intent: string, label: string) => void;
}) {
  const chipsBlock = message.reply?.blocks.find((b) => b.kind === 'chips');
  const chips = chipsBlock && chipsBlock.kind === 'chips' ? chipsBlock.chips : [];
  return (
    <>
      {/* dir="auto": an Arabic reply lays out right-to-left (first strong
          character), so mixed Arabic + English item names read in order. */}
      {message.text ? (
        <div dir="auto" className="advisor-bubble advisor-bubble--them">
          {message.text}
        </div>
      ) : null}
      {chips.length > 0 && (
        <div className="advisor-chips" dir="auto">
          {chips.map((c) => (
            <button key={c.intent + c.label} type="button" dir="auto" className="advisor-chip" onClick={() => onChip(c.intent, c.label)}>
              {c.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// NOTE (pure B2): the old AdvisorReplyView / AdvisorBlockView / AmBriefCard /
// verdictLabel rich-block renderers were removed — the thread now reads back
// from Chatwoot as plain text, so an AI message is a text bubble plus its
// contextual quick-reply chips (see AiMessage above). Verdicts/briefs/deep-links
// render as part of that text; chips are the only interactive block that
// survives the Chatwoot round-trip (carried in the hidden marker).
